import React from 'react';
import { AlertTriangle, CirclePlus, PencilLine, Plus, Trash2, Undo2, Vote } from 'lucide-react';
import ProposalDiff from './ProposalDiff';
import { DomainsField, SmartDiffInput, SmartDiffList } from './SmartDiffFields';
import {
  ACTION_LABELS,
  GovernanceSteps,
  StatusChip,
  baselineOf,
  buildOperation,
  effectiveData,
  findCircle,
  formatDate,
  operationIssue,
  operationTitle,
  parseOperations,
  sameId,
  snapshotOf,
  tensionStatus,
  useStableKeys,
} from './governance';

const ACTIONS = [
  {
    value: 'edit',
    label: ACTION_LABELS.edit,
    detail: 'Ajuster un rôle existant',
    icon: PencilLine,
    activeClass: 'border-indigo-300 bg-indigo-50 text-indigo-700',
  },
  {
    value: 'create',
    label: ACTION_LABELS.create,
    detail: 'Ajouter un nouveau rôle',
    icon: CirclePlus,
    activeClass: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  },
  {
    value: 'delete',
    label: ACTION_LABELS.delete,
    detail: 'Retirer un rôle',
    icon: Trash2,
    activeClass: 'border-red-300 bg-red-50 text-red-700',
  },
];

const VOTE_LABELS = { pending: 'En attente', accept: 'Accepté', reject: 'Refusé' };
const VOTE_TONES = {
  pending: 'bg-slate-100 text-slate-600',
  accept: 'bg-emerald-50 text-emerald-700',
  reject: 'bg-red-50 text-red-700',
};
const LOCKED_STATUSES = ['proposal', 'meeting', 'applied', 'rejected_in_meeting'];

// Un changement proposé, éditable : action, élément visé et contenu (avant/après).
export const OperationCard = ({ op, idx, total = 1, circles, disabled, onUpdate, onRemove, issue }) => {
  const currentAction = op.action || 'edit';
  const scopeCircle = findCircle(circles, op.circle_id);
  const rolesInScope = React.useMemo(() => {
    const children = (circles || []).filter((c) => sameId(c.parent_id, op.circle_id));
    const list = scopeCircle ? [scopeCircle, ...children] : children;
    const target = findCircle(circles, op.target_circle_id);
    return target && !list.some((c) => sameId(c.id, target.id)) ? [...list, target] : list;
  }, [circles, op.circle_id, op.target_circle_id, scopeCircle]);
  const baseline = baselineOf(op, circles);
  const values = effectiveData(op, baseline);
  const currentData = op.data || {};
  const currentParentId = currentData.parent_id ?? op.circle_id ?? null;
  const title = operationTitle(op, circles);

  const handleActionChange = (val) => {
    if (val === currentAction) return;
    if (val === 'create') {
      onUpdate(idx, buildOperation('create', op.circle_id));
      return;
    }
    const keepTarget = currentAction !== 'create' && !(val === 'delete' && sameId(op.target_circle_id, op.circle_id));
    const target = keepTarget ? findCircle(circles, op.target_circle_id) : null;
    onUpdate(idx, {
      ...op,
      action: val,
      target_circle_id: target ? target.id : null,
      original_data: target ? snapshotOf(target) : {},
      data: val === 'edit' && currentAction === 'edit' ? currentData : {},
    });
  };

  const handleTargetChange = (val) => {
    const target = findCircle(circles, val);
    onUpdate(idx, {
      ...op,
      target_circle_id: target ? target.id : null,
      original_data: target ? snapshotOf(target) : {},
      data: {},
    });
  };

  const updateDataField = (key, val) => {
    onUpdate(idx, { ...op, data: { ...currentData, [key]: val } });
  };

  const targetLabel = currentAction === 'create'
    ? 'Créer dans'
    : currentAction === 'delete'
      ? 'Rôle ou cercle à supprimer'
      : 'Rôle ou cercle à modifier';

  return (
    <div className={`border rounded-xl p-4 bg-slate-50 space-y-4 ${issue ? 'border-red-200' : 'border-slate-200'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs uppercase text-slate-500 font-semibold">
            Changement {total > 1 ? `${idx + 1}/${total}` : ''}
          </p>
          <p className="text-sm font-semibold text-slate-900 truncate">
            {title.verb} « {title.name} »{title.where ? ` dans « ${title.where} »` : ''}
          </p>
        </div>
        {!disabled && onRemove && (
          <button
            type="button"
            onClick={() => onRemove(idx)}
            className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1 shrink-0"
          >
            <Trash2 size={14} /> Retirer ce changement
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2" role="radiogroup" aria-label="Type de changement">
        {ACTIONS.map((actionOption) => {
          const Icon = actionOption.icon;
          const active = currentAction === actionOption.value;
          return (
            <button
              key={actionOption.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => handleActionChange(actionOption.value)}
              disabled={disabled}
              className={`min-h-[56px] rounded-xl border px-3 py-2 text-left transition ${
                active
                  ? actionOption.activeClass
                  : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
              } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              <span className="flex items-center gap-2 text-sm font-semibold">
                <Icon size={15} />
                {actionOption.label}
              </span>
              <span className="mt-1 block text-[11px] text-slate-500">{actionOption.detail}</span>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="space-y-1.5 block">
          <span className="text-xs text-slate-500 font-semibold uppercase">{targetLabel}</span>
          {currentAction === 'create' ? (
            <select
              value={currentParentId || ''}
              onChange={(e) => updateDataField('parent_id', e.target.value ? Number(e.target.value) : null)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
              disabled={disabled || !op.circle_id}
            >
              <option value="">Choisir le cercle d'accueil</option>
              {rolesInScope.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}{sameId(c.id, op.circle_id) ? ' (cercle concerné)' : ''}
                </option>
              ))}
            </select>
          ) : (
            <select
              value={op.target_circle_id || ''}
              onChange={(e) => handleTargetChange(e.target.value ? Number(e.target.value) : null)}
              className={`w-full border rounded-lg px-3 py-2 text-sm bg-white ${issue && !op.target_circle_id ? 'border-red-300' : 'border-slate-200'}`}
              disabled={disabled || !op.circle_id}
            >
              <option value="">Choisir le rôle ou cercle</option>
              {/* Le cercle concerné ne se supprime pas depuis sa propre gouvernance (c'est au cercle parent). */}
              {rolesInScope.filter((c) => currentAction !== 'delete' || !sameId(c.id, op.circle_id)).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}{sameId(c.id, op.circle_id) ? ' (le cercle lui-même)' : ''}
                </option>
              ))}
            </select>
          )}
        </label>
      </div>

      {issue && (
        <p className="flex items-center gap-1.5 text-xs text-red-600">
          <AlertTriangle size={13} /> {issue}
        </p>
      )}

      {currentAction === 'delete' && op.target_circle_id && (
        <div className="flex items-start gap-2 rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>
            Si la tension est acceptée, « {title.name} » sera supprimé ; ses éventuels sous-rôles remontent dans le
            cercle parent.
          </span>
        </div>
      )}

      {currentAction !== 'delete' && (currentAction === 'create' || op.target_circle_id) && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <SmartDiffInput
              label="Nom du rôle"
              originalValue={currentAction === 'create' ? null : (baseline.name || '')}
              currentValue={values.name}
              onChange={(v) => updateDataField('name', v)}
              placeholder="Nom du rôle"
              disabled={disabled}
            />
            <SmartDiffInput
              label="Raison d'être"
              originalValue={currentAction === 'create' ? null : (baseline.purpose || '')}
              currentValue={values.purpose}
              onChange={(v) => updateDataField('purpose', v)}
              placeholder="Pourquoi ce rôle existe"
              multiline
              disabled={disabled}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <DomainsField
              originalDomains={currentAction === 'create' ? null : (baseline.domains || [])}
              currentDomains={values.domains}
              onChangeDomains={(list) => updateDataField('domains', list)}
              originalKeys={currentAction === 'create' ? null : (baseline.reserved_domains || [])}
              currentKeys={values.reserved_domains}
              onChangeKeys={(keys) => updateDataField('reserved_domains', keys)}
              disabled={disabled}
            />
            <SmartDiffList
              label="Redevabilités"
              originalList={currentAction === 'create' ? null : (baseline.accountabilities || [])}
              currentList={values.accountabilities}
              onChange={(list) => updateDataField('accountabilities', list)}
              disabled={disabled}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Résumé figé d'une tension proposée, en réunion ou décidée.
const LockedTension = ({ tensionDraft, ops, circles, tensionVotes, tensionVotesLoading, isAuthor, onWithdraw, onDelete, onRework }) => {
  const status = tensionStatus(tensionDraft);
  const circle = findCircle(circles, tensionDraft.circle_id);
  const expressedBy = findCircle(circles, tensionDraft.expressed_by_role_id);
  const votes = Array.isArray(tensionVotes) ? tensionVotes : [];
  const autoAccept = formatDate(votes.find((v) => v?.decision === 'pending' && v?.auto_accept_at)?.auto_accept_at);
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <StatusChip tension={tensionDraft} />
          <h3 className="text-lg font-bold text-slate-900">{tensionDraft.title || 'Tension sans titre'}</h3>
          {tensionDraft.summary && (
            <p className="text-sm text-slate-600">
              <span className="font-semibold text-slate-700">Ce qui coince :</span> {tensionDraft.summary}
            </p>
          )}
          <p className="text-xs text-slate-500">
            Cercle concerné : {circle?.name || '—'}
            {expressedBy ? ` · ressentie depuis le rôle « ${expressedBy.name} »` : ''}
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          {status.key === 'proposal' && isAuthor && (
            <button
              type="button"
              onClick={() => onWithdraw(tensionDraft.id)}
              className="px-3 py-2 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 flex items-center text-sm"
              title="Retirer la proposition (retour en brouillon, votes effacés)"
            >
              Retirer du vote
            </button>
          )}
          {status.key === 'proposal' && tensionDraft.id && (
            <button
              type="button"
              onClick={() => onDelete(tensionDraft.id)}
              className="px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 flex items-center text-sm"
            >
              <Trash2 size={14} className="mr-1" /> Supprimer
            </button>
          )}
        </div>
      </div>

      <GovernanceSteps tension={tensionDraft} />

      {status.key === 'proposal' && (
        <p className="rounded-xl border border-purple-100 bg-purple-50/60 px-3 py-2 text-xs text-purple-800">
          Au vote : chaque membre du cercle accepte ou refuse. Un seul refus la renvoie en brouillon
          {autoAccept ? ` ; sans réponse, accepté automatiquement le ${autoAccept}` : ''}.
        </p>
      )}
      {status.key === 'meeting' && (
        <p className="rounded-xl border border-indigo-100 bg-indigo-50/60 px-3 py-2 text-xs text-indigo-800">
          Traitée en réunion de gouvernance : le secrétaire peut l'ajuster avec le cercle avant la décision.
          {tensionDraft.decision_error ? ` Dernier essai non appliqué : ${tensionDraft.decision_error}` : ''}
        </p>
      )}
      {status.key === 'applied' && (
        <p className="rounded-xl border border-emerald-100 bg-emerald-50/60 px-3 py-2 text-xs text-emerald-800">
          Acceptée : la structure est à jour.
        </p>
      )}
      {status.key === 'rejected' && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-red-100 bg-red-50/60 px-3 py-2 text-xs text-red-800">
          <span>Refusée en réunion de gouvernance{tensionDraft.rejected_at ? ` le ${formatDate(tensionDraft.rejected_at, false)}` : ''}.</span>
          {isAuthor && onRework && (
            <button
              type="button"
              onClick={onRework}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-red-200 text-red-700 font-semibold hover:bg-red-50"
            >
              <Undo2 size={13} /> Retravailler dans un nouveau brouillon
            </button>
          )}
        </div>
      )}

      <ProposalDiff
        operations={ops}
        circles={circles}
        original={tensionDraft.action === 'create' ? null : tensionDraft.original_data}
        proposed={tensionDraft.proposed_data}
        action={tensionDraft.action}
        circleLabel={tensionDraft.original_data?.name}
      />

      {isAuthor && status.key === 'proposal' && (
        <div className="border border-slate-200 rounded-xl p-3 bg-white">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-slate-800">
              Votes ({votes.filter((v) => v.decision !== 'pending').length}/{votes.length})
            </p>
            {tensionVotesLoading && <span className="text-xs text-slate-500">Actualisation…</span>}
          </div>
          {votes.length === 0 ? (
            <p className="text-xs text-slate-500">Aucun vote encore enregistré.</p>
          ) : (
            <div className="space-y-2">
              {votes.map((v) => (
                <div
                  key={v.id}
                  className="flex items-center justify-between text-sm bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5"
                >
                  <span className="font-medium text-slate-800">{v.voter_name || 'Votant'}</span>
                  <span className={`text-xs px-2 py-1 rounded-full ${VOTE_TONES[v.decision] || VOTE_TONES.pending}`}>
                    {VOTE_LABELS[v.decision] || VOTE_LABELS.pending}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const TensionEditor = ({
  tensionDraft,
  circles,
  user,
  tensionVotes,
  tensionVotesLoading,
  tensionsLoading,
  onUpdateField,
  onDelete,
  onPropose,
  onWithdraw,
  onCancel,
  onSave,
  mode = 'tension',
  readOnly = false,
  expressedRoleOptions,
  defaultCircleId,
}) => {
  const [showIssues, setShowIssues] = React.useState(false);
  const ops = React.useMemo(() => parseOperations(tensionDraft?.operations), [tensionDraft?.operations]);
  // Toujours la dernière liste : deux mises à jour dans le même tick ne s'écrasent pas.
  const opsRef = React.useRef(ops);
  opsRef.current = ops;
  const opKeys = useStableKeys(ops.length);

  const effectiveUserId = user?.employee_id ?? user?.id;
  const assignedRoles = React.useMemo(() => {
    if (!effectiveUserId) return [];
    const userId = String(effectiveUserId);
    return (circles || []).filter((circle) => {
      if (!circle) return false;
      if (circle.owner_id !== undefined && circle.owner_id !== null && String(circle.owner_id) === userId) return true;
      return Array.isArray(circle.owner_ids) && circle.owner_ids.some((id) => String(id) === userId);
    });
  }, [circles, effectiveUserId]);
  const circleOptions = React.useMemo(() => {
    const list = circles || [];
    const childrenByParent = {};
    list.forEach((circle) => {
      if (!circle?.parent_id) return;
      const key = String(circle.parent_id);
      childrenByParent[key] = (childrenByParent[key] || 0) + 1;
    });
    return list.filter((circle) => {
      if (!circle) return false;
      const hasParent = circle.parent_id !== null && circle.parent_id !== undefined && circle.parent_id !== '';
      return !hasParent || Boolean(childrenByParent[String(circle.id)]);
    });
  }, [circles]);

  const updateOps = React.useCallback((next) => {
    opsRef.current = next;
    if (onUpdateField) onUpdateField('operations', next);
  }, [onUpdateField]);

  const applyCircleContext = React.useCallback((circleId, action = 'edit') => {
    if (!circleId || !onUpdateField) return;
    const snapshot = snapshotOf(findCircle(circles, circleId));
    onUpdateField('circle_id', Number(circleId));
    onUpdateField('original_data', snapshot);
    onUpdateField('proposed_data', snapshot);
    updateOps([buildOperation(action, Number(circleId))]);
  }, [circles, onUpdateField, updateOps]);

  // Brouillon sans cercle (réunion) : on part du cercle de la réunion ou du premier cercle.
  const draftStatus = tensionDraft?.status;
  const draftCircleId = tensionDraft?.circle_id;
  const draftAction = tensionDraft?.action;
  const hasDraft = Boolean(tensionDraft);
  React.useEffect(() => {
    if (!hasDraft || readOnly || LOCKED_STATUSES.includes(draftStatus)) return;
    if (draftCircleId) return;
    const fallbackCircleId = defaultCircleId || circleOptions[0]?.id;
    if (!fallbackCircleId) return;
    applyCircleContext(fallbackCircleId, draftAction || 'edit');
  }, [applyCircleContext, circleOptions, defaultCircleId, draftAction, draftCircleId, draftStatus, hasDraft, readOnly]);

  // Brouillon modifié depuis le dernier enregistrement ? (on ne propose au vote que ce qui est enregistré)
  const signature = JSON.stringify({
    title: tensionDraft?.title || '',
    summary: tensionDraft?.summary || '',
    role: String(tensionDraft?.expressed_by_role_id ?? ''),
    circle: String(tensionDraft?.circle_id ?? ''),
    ops,
  });
  const savedRef = React.useRef({ key: null, signature: null });
  const savedKey = `${tensionDraft?.id ?? 'new'}|${tensionDraft?.updated_at ?? ''}`;
  if (savedRef.current.key !== savedKey) savedRef.current = { key: savedKey, signature };
  const isDirty = savedRef.current.signature !== signature;

  if (!tensionDraft) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400 text-sm text-center px-6">
        Sélectionne une tension ou crée-en une pour la prévisualiser.
      </div>
    );
  }

  const status = String(tensionDraft.status || 'draft').toLowerCase();
  const isAuthor = Boolean(effectiveUserId && tensionDraft.author_id && String(effectiveUserId) === String(tensionDraft.author_id));
  const isMeetingForm = mode === 'meeting';
  const expressedRoles = expressedRoleOptions && expressedRoleOptions.length > 0 ? expressedRoleOptions : assignedRoles;

  const handleUpdateOp = (idx, nextOp) => {
    updateOps(opsRef.current.map((op, i) => (i === idx ? { ...nextOp, circle_id: tensionDraft.circle_id } : op)));
  };
  const handleRemoveOp = (idx) => {
    opKeys.remove(idx);
    updateOps(opsRef.current.filter((_, i) => i !== idx));
  };
  const addOperation = (action = 'edit') => {
    const circleId = tensionDraft.circle_id || defaultCircleId || circleOptions[0]?.id || null;
    if (!tensionDraft.circle_id && circleId && onUpdateField) {
      const snapshot = snapshotOf(findCircle(circles, circleId));
      onUpdateField('circle_id', Number(circleId));
      onUpdateField('original_data', snapshot);
      onUpdateField('proposed_data', snapshot);
    }
    opKeys.append();
    updateOps([...opsRef.current, buildOperation(action, circleId)]);
  };

  // Tension refusée en réunion : on repart d'un nouveau brouillon (l'historique de la réunion reste intact).
  const reworkAsNewDraft = () => {
    if (!onUpdateField) return;
    const fresh = ops.map((op) => {
      if ((op.action || 'edit') === 'create') return { ...op, original_data: {} };
      const target = findCircle(circles, op.target_circle_id);
      return { ...op, original_data: target ? snapshotOf(target) : (op.original_data || {}) };
    });
    ['id', 'meeting_id', 'rejected_at', 'rejected_by_name', 'decision_error', 'updated_at'].forEach((key) => onUpdateField(key, null));
    onUpdateField('status', 'draft');
    onUpdateField('operations', fresh);
  };

  if (LOCKED_STATUSES.includes(status)) {
    return (
      <LockedTension
        tensionDraft={tensionDraft}
        ops={ops}
        circles={circles}
        tensionVotes={tensionVotes}
        tensionVotesLoading={tensionVotesLoading}
        isAuthor={isAuthor}
        onWithdraw={onWithdraw}
        onDelete={onDelete}
        onRework={reworkAsNewDraft}
      />
    );
  }

  const issues = ops.map(operationIssue);
  const blocking = ops.length === 0 ? 'Ajoute au moins un changement (créer, modifier ou supprimer un rôle).' : null;
  const handleSave = () => {
    if (blocking || issues.some(Boolean)) {
      setShowIssues(true);
      return;
    }
    setShowIssues(false);
    if (onSave) onSave();
  };
  const isLocked = readOnly;
  const statusInfo = tensionStatus(tensionDraft);

  return (
    <div className="space-y-4">
      {!isMeetingForm && (
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <StatusChip tension={tensionDraft} />
            <p className="text-xs text-slate-500">
              {tensionDraft.id ? "Brouillon enregistré, visible de toi seul jusqu'à ce que tu le proposes." : 'Nouveau brouillon.'}
            </p>
          </div>
          {tensionDraft.id && (
            <button
              type="button"
              onClick={() => onDelete(tensionDraft.id)}
              className="px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 flex items-center text-sm shrink-0"
            >
              <Trash2 size={14} className="mr-1" /> Supprimer
            </button>
          )}
        </div>
      )}
      {!isMeetingForm && <GovernanceSteps tension={tensionDraft} />}

      {statusInfo.key === 'reworked' && (
        <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Refusée par {tensionDraft.rejected_by_name || 'un votant'}
          {tensionDraft.rejected_at ? ` le ${formatDate(tensionDraft.rejected_at, false)}` : ''} — à retravailler.
          Ajuste la proposition, enregistre, puis relance le vote ou porte-la en réunion de gouvernance.
        </p>
      )}
      {statusInfo.key === 'failed' && (
        <p className="rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-800">
          Acceptée mais non appliquée : {tensionDraft.decision_error} Corrige la proposition puis relance.
        </p>
      )}

      <label className="block space-y-1.5">
        <span className="text-xs text-slate-500 font-semibold uppercase">Titre</span>
        <input
          value={tensionDraft.title || ''}
          onChange={(e) => onUpdateField('title', e.target.value)}
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-semibold"
          readOnly={isLocked}
          placeholder="Titre court (proposé automatiquement si vide)"
        />
      </label>

      <label className="block space-y-1.5">
        <span className="text-xs text-slate-500 font-semibold uppercase">Ce qui coince</span>
        <textarea
          value={tensionDraft.summary || ''}
          onChange={(e) => onUpdateField('summary', e.target.value)}
          rows={2}
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
          readOnly={isLocked}
          placeholder="Décris la situation qui gêne : ce qui manque, ce qui est flou, qui fait quoi…"
        />
      </label>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="flex flex-col gap-1.5">
          <span className="text-xs text-slate-500 font-semibold uppercase">Rôle depuis lequel tu ressens la tension</span>
          <select
            value={tensionDraft.expressed_by_role_id ? String(tensionDraft.expressed_by_role_id) : ''}
            onChange={(e) => onUpdateField('expressed_by_role_id', e.target.value || null)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
            disabled={isLocked}
          >
            <option value="">Choisir un de tes rôles (facultatif)</option>
            {expressedRoles.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.name}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-xs text-slate-500 font-semibold uppercase">Cercle concerné</span>
          <select
            value={tensionDraft.circle_id ? String(tensionDraft.circle_id) : ''}
            onChange={(e) => {
              const val = e.target.value ? Number(e.target.value) : null;
              if (val) {
                applyCircleContext(val, ops[0]?.action || tensionDraft.action || 'edit');
              } else {
                onUpdateField('circle_id', null);
                updateOps([]);
              }
            }}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
            disabled={isLocked}
          >
            <option value="">Choisir un cercle</option>
            {circleOptions.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="border border-slate-200 rounded-xl p-4 bg-white space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase text-slate-500 font-semibold">Changements proposés</p>
            <p className="text-[11px] text-slate-500">
              Un changement suffit souvent. Ajoutes-en plusieurs s'ils doivent être décidés ensemble.
            </p>
          </div>
          {ops.length > 0 && (
            <button
              type="button"
              onClick={() => addOperation('edit')}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 shrink-0"
              disabled={isLocked || !tensionDraft.circle_id}
            >
              <Plus size={14} /> Ajouter un changement
            </button>
          )}
        </div>

        {ops.length === 0 && (
          <div className={`rounded-xl border border-dashed p-3 ${showIssues ? 'border-red-200 bg-red-50/40' : 'border-slate-200 bg-slate-50'}`}>
            <p className="text-sm text-slate-600 mb-3">
              {tensionDraft.circle_id
                ? 'Que proposes-tu de changer dans ce cercle ?'
                : "Choisis d'abord le cercle concerné, puis ce que tu proposes de changer."}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {ACTIONS.map((actionOption) => {
                const Icon = actionOption.icon;
                return (
                  <button
                    key={actionOption.value}
                    type="button"
                    onClick={() => addOperation(actionOption.value)}
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-sm hover:border-indigo-200 hover:bg-indigo-50 disabled:opacity-50"
                    disabled={isLocked || !tensionDraft.circle_id}
                  >
                    <span className="flex items-center gap-2 font-semibold text-slate-800">
                      <Icon size={15} />
                      {actionOption.label}
                    </span>
                    <span className="mt-1 block text-[11px] text-slate-500">{actionOption.detail}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="space-y-3">
          {ops.map((op, idx) => (
            <OperationCard
              key={opKeys.keys[idx]}
              op={{ ...op, circle_id: tensionDraft.circle_id }}
              idx={idx}
              total={ops.length}
              circles={circles}
              disabled={isLocked}
              onUpdate={handleUpdateOp}
              onRemove={handleRemoveOp}
              issue={showIssues ? issues[idx] : null}
            />
          ))}
        </div>
        {showIssues && blocking && <p className="text-xs text-red-600">{blocking}</p>}
      </div>

      {(!isMeetingForm || !readOnly) && (
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 text-sm">
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 disabled:opacity-60"
            disabled={tensionsLoading}
          >
            {isMeetingForm ? 'Ajouter à la réunion' : 'Enregistrer le brouillon'}
          </button>
        </div>
      )}

      {!isMeetingForm && tensionDraft.id && status === 'draft' && (
        <div className="rounded-xl border border-indigo-100 bg-indigo-50/50 p-3 space-y-2">
          <p className="text-sm font-semibold text-slate-800">Étape 2 : la proposer</p>
          <p className="text-xs text-slate-600">
            <span className="font-semibold">Au vote</span> : les membres du cercle répondent quand ils veulent ; un
            refus la renvoie ici, sans réponse c'est un accord. <span className="font-semibold">En réunion</span> :
            importe-la dans la réunion de gouvernance du cercle, la décision se prend en direct.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => onPropose(tensionDraft.id)}
              disabled={isDirty || tensionsLoading}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-semibold disabled:opacity-50"
            >
              <Vote size={14} /> Proposer au vote
            </button>
            {isDirty && <span className="text-xs text-slate-500">Enregistre d'abord tes modifications.</span>}
          </div>
        </div>
      )}
    </div>
  );
};

export default TensionEditor;
